import React, { useState, useEffect, useRef } from "react";

interface CustomDropDownProps {
  label?: string;
  name: string;
  options: { label: string; value: string | number }[];
  register: any;
  errors?: any;
  disabled?: boolean;
  defaultValue?: string | number;
  placeholder?: string;
  className?: string;
  onChange?: (value: string | number) => void;
  control?: any;
}

const CustomDropDown: React.FC<CustomDropDownProps> = ({
  label,
  name,
  options,
  register,
  errors,
  disabled = false,
  defaultValue = "",
  placeholder,
  className = "",
  onChange,
}) => {
  const [selectedValue, setSelectedValue] = useState<string | number>(defaultValue);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Get selected option label
  const getSelectedLabel = () => {
    const selectedOption = options.find(option => option.value === selectedValue);
    return selectedOption ? selectedOption.label : "";
  };

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option?.label?.toString().toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Update the selected value whenever defaultValue changes
  useEffect(() => {
    if (defaultValue !== undefined && defaultValue !== null) {
      setSelectedValue(defaultValue);
    }
  }, [defaultValue]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Handle option selection
  const handleSelectOption = (value: string | number) => {
    setSelectedValue(value);
    setIsOpen(false);
    setSearchTerm("");

    // Create a synthetic event to pass to register
    const syntheticEvent = {
      target: {
        name,
        value
      }
    } as React.ChangeEvent<HTMLSelectElement>;

    // Call the register's onChange
    register(name).onChange(syntheticEvent);

    // Call optional onChange callback
    if (onChange) {
      onChange(value);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0));
        break;
      case "Enter":
        e.preventDefault();
        if (filteredOptions[highlightedIndex]) {
          handleSelectOption(filteredOptions[highlightedIndex].value);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  // Combine register with custom props
  const registerProps = register(name);

  return (
    <div className={`w-full ${className} relative`} ref={dropdownRef}>
      {label && (
        <label
          htmlFor={name}
          className="input-label block text-xs font-medium text-gray-700 mb-1"
        >
          {label}
        </label>
      )}

      {/* Hidden select for form handling */}
      <select
        {...registerProps}
        style={{ display: 'none' }}
        value={selectedValue || ""}
        disabled={disabled}
        aria-hidden="true"
      >
        <option value={selectedValue || ""}>{getSelectedLabel()}</option>
      </select>

      {/* Custom dropdown UI */}
      <div
        className={`relative ${disabled ? 'opacity-60 pointer-events-none' : ''}`}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <div
          className={`w-full px-3 py-2 border rounded-md bg-white cursor-pointer flex items-center justify-between border-gray-300
            focus:outline-none hover:border-blue-300 transition-all text-xs
            ${isOpen ? "ring-2 ring-blue-500 border-blue-500" : ""}
            ${errors?.[name] ? "border-red-500" : ""}
          `}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <span className={selectedValue ? "text-gray-900" : "text-gray-400"}>
            {selectedValue ? getSelectedLabel() : (placeholder || `Select ${label || "option"}`)}
          </span>
          <svg
            className={`fill-current h-3 w-3 transition-transform duration-200 ${isOpen ? "transform rotate-180" : ""}`}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </div>

        {/* Dropdown menu */}
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg overflow-hidden">
            {/* Search input */}
            <div className="px-2 py-1 border-b">
              <input
                ref={searchInputRef}
                type="text"
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setHighlightedIndex(0);
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            {/* Options list */}
            <div className="max-h-40 overflow-y-auto">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => (
                  <div
                    key={option.value}
                    className={`px-3 py-1.5 cursor-pointer text-xs hover:bg-blue-50 ${highlightedIndex === index ? "bg-blue-50" : ""
                      } ${selectedValue === option.value ? "bg-blue-100" : ""}`}
                    onClick={() => handleSelectOption(option.value)}
                  >
                    {option.label}
                  </div>
                ))
              ) : (
                <div className="px-3 py-1.5 text-xs text-gray-500">No options found</div>
              )}
            </div>

            {/* Show more options indicator */}
            {filteredOptions.length > 5 && (
              <div className="px-3 py-1 text-xs text-center text-gray-500 border-t">
                Scroll to see more options
              </div>
            )}
          </div>
        )}
      </div>

      {errors?.[name] && (
        <p className="input-error-message mt-1 text-xs text-red-600">
          {errors[name]?.message}
        </p>
      )}
    </div>
  );
};

export default CustomDropDown;